import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";
import { ApplicationStore } from "src/store";
import { LeasePayment } from "Types/api/LeasePayment";
import { LoanPayment } from "Types/api/LoanPayment";
import { VehiclePaymentType } from "Types/api/VehiclePayments";
import { LeaseOffer, LoanOffer } from "Types/state";

export const hasVehicle = (state: ApplicationStore, vin: string) => vin in state.vehicles.entities;

const getVehiclesOrder = (state: ApplicationStore) => state.vehicles.order;
const getVehiclesEntities = (state: ApplicationStore) => state.vehicles.entities;
// TODO: Adding this to mark everywhere we pass an array of vehicles to FEA. We're breaking connected
//  component memoization and should fix this at some point
// DO NOT USE except to pass vehicles to AnalyticsFEA.
export const getVehiclesArrayForFEA = createSelector(getVehiclesOrder, getVehiclesEntities, (vins, vehicles) =>
    vins.map((vin) => vehicles[vin])
);

// DO NOT USE ANYMORE. Get individual properties. Only adding this for existing components that needed it for a non FEA reason
export const getVehiclesArrayUntilRefactor = createSelector(getVehiclesOrder, getVehiclesEntities, (vins, vehicles) =>
    vins.map((vin) => vehicles[vin])
);

export const getVehicle = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin] ?? null;

export const isVehicleLoading = (state: ApplicationStore, vin: string) =>
    !!(!(vin in state.vehicles.entities) || state.vehicles.entities[vin]?.loading);

export const isVehicleUpdating = (state: ApplicationStore, vin: string) => !!state.vehicles.entities[vin]?.isUpdating;

export const getVehicleMake = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.make ?? null;

export const getVehicleTitle = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.title;

export const getVehicleType = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.type;

export const getVehicleStock = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.stock;

export const getVehicleOdometer = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.odometer;

export const getVehicleThumbnail = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.thumbnail;

export const getPayments = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin]?.payments;

export const getSelectedPaymentType = (state: ApplicationStore, vin: string): VehiclePaymentType =>
    state.vehicles.entities[vin]?.payments.selected;

export const getSelectedPayment = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin]?.payments[getSelectedPaymentType(state, vin)]!;

export const getSelectedPaymentOffers = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin]?.payments[getSelectedPaymentType(state, vin)]?.offers ?? [];

export const getSelectedOfferIndex = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin]?.payments[getSelectedPaymentType(state, vin)]?.selected_offer ?? null;

export const getOfferByIndex = (state: ApplicationStore, vin: string, index: number) => {
    const type = getSelectedPaymentType(state, vin);
    const offers = state.vehicles.entities[vin]?.payments[type]?.offers;
    if (!offers || !offers[index]) {
        throw Error(`Vehicle ${vin} has no ${type} offer ${index}`);
    }
    return offers[index];
};

export const getSelectedOffer = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin]?.payments[getSelectedPaymentType(state, vin)]?.offers?.[
        getSelectedOfferIndex(state, vin) ?? 0
    ] ?? null;

// This is the old-fashioned reformat of finance settings from 2018. This is not receiving new features:  conditional_rebates and payment_frequencies are only on integration_settings.Financing
export const getVehicleFinanceSettings = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin].account.finance_settings;

export const getLeaseMiles = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin]?.payments.lease?.miles ?? getVehicleFinanceSettings(state, vin).lease.miles.default;

export const getLeaseMileOptions = (state: ApplicationStore, vin: string) =>
    getVehicleFinanceSettings(state, vin).lease.miles.options;

export const getSelectedPaymentTerm = (state: ApplicationStore, vin: string) =>
    (getSelectedPayment(state, vin) as LoanPayment & LeasePayment).term!;

export const getSelectedPaymentTermOptions = (state: ApplicationStore, vin: string) =>
    getVehicleFinanceSettings(state, vin)[getSelectedPaymentType(state, vin) as "lease" | "loan"].term.options;

export const getVehiclePrice = (state: ApplicationStore, vin: string) =>
    state.vehicles.entities[vin]?.payments.dealer
        ? state.vehicles.entities[vin]?.payments.dealer?.price ?? 0
        : getSelectedPayment(state, vin)?.price ?? 0;

export const getVehicleMsrp = (state: ApplicationStore, vin: string) => state.vehicles.entities[vin].msrp!;

export const getOfferIncentives = createCachedSelector(getSelectedOffer, (offer) => {
    return (
        offer?.rebates.reduce(
            (total: number, rebate: { name: string; value: number; id?: number }) => (total += rebate.value),
            0
        ) ?? 0
    );
})((state, vin) => vin);

// This is the same as APR
export const getInterestRate = (state: ApplicationStore, vin: string, offerIndex?: number) => {
    const offer = offerIndex ? getOfferByIndex(state, vin, offerIndex) : getSelectedOffer(state, vin);
    return (offer as LoanOffer & LeaseOffer)?.interest_rate;
};

export const getLender = (state: ApplicationStore, vin: string) =>
    (getSelectedOffer(state, vin) as LoanOffer & LeaseOffer)?.lender;

export const getDownPayment = (state: ApplicationStore, vin: string) =>
    (getSelectedPayment(state, vin) as LoanPayment | LeasePayment).down_payment ?? 0;

export const getTotalFees = createCachedSelector(getSelectedOffer, (offer) => {
    return offer?.fees.reduce((total: number, fee: { name: string; value: number }) => (total += fee.value), 0) ?? 0;
})((state, vin) => vin);

export const getTotalNonMonthlyTaxes = createCachedSelector(getSelectedOffer, (offer) => {
    return (
        offer?.taxes.reduce(
            (
                total: number,
                tax: {
                    name: string;
                    value: number;
                    collected?: "upfront" | "monthly";
                }
            ) => {
                if (tax.collected !== "upfront") {
                    return total;
                }

                return (total += tax.value);
            },
            0
        ) ?? 0
    );
})((state, vin) => vin);

export const getTotalMonthlyTaxes = createCachedSelector(getSelectedOffer, (offer) => {
    return (
        offer?.taxes.reduce(
            (
                total: number,
                tax: {
                    name: string;
                    value: number;
                    collected?: "upfront" | "monthly";
                }
            ) => {
                if (tax.collected !== "monthly") {
                    return total;
                }

                return (total += tax.value);
            },
            0
        ) ?? 0
    );
})((state, vin) => vin);

export const getPaymentFrequency = (state: ApplicationStore, vin: string) =>
    (getSelectedOffer(state, vin) as LoanOffer & LeaseOffer)?.frequency;

export const getOfferDisclaimer = (state: ApplicationStore, vin: string, offerIndex?: number) => {
    const offer = offerIndex ? getOfferByIndex(state, vin, offerIndex) : getSelectedOffer(state, vin);
    return offer?.disclaimer || "";
};

export const getPaymentTypeMatrix = (state: ApplicationStore, vin: string) => {
    const payments = getPayments(state, vin);

    return {
        cash: !!payments?.cash?.enabled,
        loan: !!payments?.loan?.enabled,
        lease: !!payments?.lease?.enabled,
    };
};

export const getSelectedFrequency = (state: ApplicationStore, vin: string, paymentType: string) => {
    return state.vehicles.entities[vin]?.payments[paymentType]?.frequency ?? "monthly";
};
